import React from "react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";

const Pricing = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16">
        <h1 className="text-4xl md:text-6xl font-bold text-center mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-xl text-muted-foreground text-center mb-16 max-w-3xl mx-auto">
          Why waste time on busywork? Choose the plan that makes the grind suck less. 
          Whether you're just dipping your toes or ready to go full anti-work hero, we've got you covered.
        </p>
        
        {/* Pricing Cards Grid */}
        <div className="grid md:grid-cols-4 gap-8 mt-16 max-w-7xl mx-auto">
          {/* Free Plan */}
          <div className="border rounded-lg p-8 bg-card">
            <h3 className="text-2xl font-semibold mb-2">Free</h3>
            <p className="text-3xl font-bold mb-2">$0<span className="text-lg font-normal text-muted-foreground">/mo</span></p>
            <p className="text-sm text-muted-foreground mb-6">For when you're curious but not ready to commit</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>3 meetings/month</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>Basic action items</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>Thought capture</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>1GB storage</span>
              </li>
              {/*<li className="flex items-center gap-2">
                <X className="h-5 w-5 text-red-500" />
                <span className="text-muted-foreground">AI chatbot</span>
              </li>*/}
            </ul>
            <Button asChild variant="outline" className="w-full">
              <Link to="/sign-up">Start Free</Link>
            </Button>
          </div>
          
          {/* Basic Plan */}
          <div className="border rounded-lg p-8 bg-card">
            <h3 className="text-2xl font-semibold mb-2">Basic</h3>
            <p className="text-3xl font-bold mb-2">$9<span className="text-lg font-normal text-muted-foreground">/mo</span></p>
            <p className="text-sm text-muted-foreground mb-6">You’ve got stuff to do—and you’d rather not do it all yourself.</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>10 meetings/month</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>60 min/meeting</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>AI summaries</span>
              </li>
              {/*<li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>5 AI queries/day</span>
              </li>*/}
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>5GB storage</span>
              </li>
            </ul>
            <Button asChild className="w-full">
              <Link to="/sign-up">Cut the Hustle</Link>
            </Button>
          </div>
          
          {/* Pro Plan */}
          <div className="border-2 border-primary rounded-lg p-8 bg-card relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm">
              Most Popular
            </div>
            <h3 className="text-2xl font-semibold mb-2">Pro</h3>
            <p className="text-3xl font-bold mb-2">$29<span className="text-lg font-normal text-muted-foreground">/mo</span></p>
            <p className="text-sm text-muted-foreground mb-6">For when you’re tired of wasting time and need some real firepower.</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>30 meetings/month</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>90 min/meeting</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>Advanced analysis</span>
              </li>
              {/*<li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>Knowledge base</span>
              </li>*/}
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>25GB storage</span>
              </li>
            </ul>
            <Button asChild className="w-full">
              <Link to="/sign-up">Save Time</Link>
            </Button>
          </div>
          
          {/* Premium Plan */}
          <div className="border rounded-lg p-8 bg-card">
            <h3 className="text-2xl font-semibold mb-2">Premium</h3>
            <p className="text-3xl font-bold mb-2">$59<span className="text-lg font-normal text-muted-foreground">/mo</span></p>
            <p className="text-sm text-muted-foreground mb-6">The ultimate plan for doing less and getting more credit.</p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>Unlimited meetings</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>120 min/meeting</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>Advanced transcription</span>
              </li>
              {/*<li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>Unlimited AI</span>
              </li>*/}
              <li className="flex items-center gap-2">
                <Check className="h-5 w-5 text-green-500" />
                <span>50GB storage</span>
              </li>
            </ul>
            <Button asChild className="w-full">
              <Link to="/sign-up">Be the Hero</Link>
            </Button>
          </div>
        </div>

        {/* Detailed Plan Descriptions */}
        <div className="mt-32 mb-32 space-y-16">
          <h2 className="text-3xl font-bold text-center mb-16">Choose Your Path to Freedom</h2>
          
          {/* Free Plan Details */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Free Plan: Get Started</span>
                <span className="text-2xl font-bold">$0/mo</span>
              </CardTitle>
              <CardDescription className="text-lg">
                For when you're curious but not ready to commit. It's free—like your time should be.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold mb-4">What's Included</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Record and transcribe up to 3 meetings/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Basic action item management: Add, track, and organize manually</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Thought capture: Jot down ideas and keep them in one place</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Retention: 14 days (then poof—gone forever)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Storage: 1GB (because you're keeping it light)</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">What's Not Included</h4>
                <ul className="space-y-2">
                  {/*<li className="flex items-start gap-2">
                    <X className="h-5 w-5 mt-1 text-red-500 shrink-0" />
                    <span>AI chatbot: Sorry, but you're on your own for now</span>
                  </li>*/}
                  <li className="flex items-start gap-2">
                    <X className="h-5 w-5 mt-1 text-red-500 shrink-0" />
                    <span>Advanced summaries, tagging, or automation: This plan's as basic as it gets</span>
                  </li>
                </ul>
                <Button asChild variant="outline" className="mt-8">
                  <Link to="/sign-up" className="flex items-center gap-2">
                    Start Free, Stay Free <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Basic Plan Details */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Basic Plan</span>
                <span className="text-2xl font-bold">$9/mo</span>
              </CardTitle>
              <CardDescription className="text-lg">
                Because you've got stuff to do—and you'd rather not do it all yourself.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold mb-4">What's Included</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Record and transcribe up to 10 meetings/month (60 min max per meeting)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Detailed meeting summaries: AI organizes your chaos into something useful</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Automated action item extraction: Turn rambling into tasks</span>
                  </li>
                  {/*<li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Tagging: Manually tag meetings, tasks, and notes for easy organization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>AI chatbot: 5 queries/day. Think of it as dipping your toe into AI land</span>
                  </li>*/}
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Retention: 60 days</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Storage: 5GB</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">What's Not Included</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <X className="h-5 w-5 mt-1 text-red-500 shrink-0" />
                    <span>Advanced analysis or categorization: You're still doing some heavy lifting</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <X className="h-5 w-5 mt-1 text-red-500 shrink-0" />
                    <span>Task automation: Your tasks are all you, champ</span>
                  </li>
                </ul>
                <Button asChild className="mt-8">
                  <Link to="/sign-up" className="flex items-center gap-2">
                    Cut the Hustle, Get Back to Binging Netflix <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Pro Plan Details */}
          <Card className="border-2 border-primary">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Pro Plan</span>
                <span className="text-2xl font-bold">$29/mo</span>
              </CardTitle>
              <CardDescription className="text-lg">
                For when you're tired of wasting time and need some real firepower.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold mb-4">What's Included</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Record and transcribe up to 30 meetings/month (90 min max per meeting)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Comprehensive summaries with sentiment analysis and decision tracking: Yes, we know who sounded annoyed</span>
                  </li>
                  {/*<li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Advanced action items: Sub-tasks, dependencies, resource links, and timelines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Unlimited tagging and categorization: Hierarchical filters to keep you so organized</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Knowledge Base Integration: Upload docs, links, or files to make AI smarter</span>
                  </li>*/}
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Retention: Unlimited (like your ambition)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Storage: 25GB (that's a lot of meetings)</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">What's Not Included</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <X className="h-5 w-5 mt-1 text-red-500 shrink-0" />
                    <span>Proactive AI task suggestions: You're in charge—for now</span>
                  </li>
                  {/*<li className="flex items-start gap-2">
                    <X className="h-5 w-5 mt-1 text-red-500 shrink-0" />
                    <span>AI completing tasks: You're the warrior here, after all</span>
                  </li>*/}
                </ul>
                <Button asChild className="mt-8">
                  <Link to="/sign-up" className="flex items-center gap-2">
                    Save Time, Rule the World <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Premium Plan Details */}
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Premium Plan</span>
                <span className="text-2xl font-bold">$59/mo</span>
              </CardTitle>
              <CardDescription className="text-lg">
                The ultimate plan for doing less and getting more credit.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold mb-4">What's Included</h4>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Unlimited meetings (120 min max per meeting) with real-time transcription</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>AI-driven insights: Proactive recommendations, trends, and follow-up ideas</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Advanced action items with AI-generated steps and timelines: We'll even tell you how to do it</span>
                  </li>
                  {/*<li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Recurring task automation: Set it and forget it (until AI reminds you)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>AI chatbot: Unlimited queries with advanced brainstorming and strategy tools</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Automatic tagging and categorization: Let AI handle the organizing for you</span>
                  </li>*/}
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Retention: Unlimited</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-5 w-5 mt-1 text-green-500 shrink-0" />
                    <span>Storage: 50GB</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">What's Not Included</h4>
                <p className="text-muted-foreground italic">Literally nothing. You're the boss now.</p>
                <Button asChild className="mt-8">
                  <Link to="/sign-up" className="flex items-center gap-2">
                    Be the Anti-Work Hero You Were Meant to Be <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">
            Need more details? Check out our detailed feature comparison
          </p>
          <Button asChild variant="outline">
            <Link to="/pricing/compare">View Full Comparison</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Pricing;